<template>
  <div
    class="expansion-panel"
    :class="{
      divider,
      fill,
    }"
    :style="styles"
    role="region"
    :aria-labelledby="`expansion-title-${uid}`"
    :aria-description="content ? 'Erweiterbarer Bereich mit statischem Inhalt' : 'Erweiterbarer Bereich mit Slot-Inhalt'"
  >
    <div
      class="accessibility-outline expansion-panel--header text-primary"
      role="button"
      tabindex="0"
      :aria-expanded="isOpen"
      :aria-controls="`expansion-content-${uid}`"
      :aria-label="`${title} ${isOpen ? 'aufklappen' : 'zuknappen'}`"
      @click="extendContent"
      @keydown.enter.prevent="extendContent"
      @keydown.space.prevent="extendContent"
    >
      <h5
        :id="`expansion-title-${uid}`"
        :style="`order: ${layout.indexOf('title')}`"
        class="expansion-panel--header-title"
        :class="{
          'no-filler': layout.indexOf('filler') === -1,
          [getPos.title]: true,
        }"
      >
        {{ title }}
      </h5>
      <hr
        v-if="layout.indexOf('filler') !== -1"
        :style="`order: ${layout.indexOf('filler')}`"
        class="expansion-panel--header-filler"
        :class="{
          [getPos.filler]: true,
        }"
      >
      <span
        :style="`order: ${layout.indexOf('icon')}`"
        class="expansion-panel--header-icon"
        :class="{
          [getPos.icon]: true,
          extended: isExtended,
          open: isOpen,
        }"
      >
        <IoIcon
          v-show="isExtended"
          :icon="icon.close"
        />
        <IoIcon
          v-show="!isExtended"
          :icon="icon.open"
        />
      </span>
    </div>
    <div
      v-if="content"
      :id="`expansion-content-${uid}`"
      ref="content"
      class="expansion-panel--content"
      :class="{
        extended: isExtended,
        open: isOpen,
      }"
      role="region"
      v-html="content"
    />
    <div
      v-else
      :id="`expansion-content-${uid}`"
      ref="content"
      class="expansion-panel--content"
      :class="{
        extended: isExtended,
        open: isOpen,
      }"
      role="region"
    >
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: 'IoExpansionPanel',
  props: {
    title: {
      type: String,
      required: true,
    },
    content: {
      type: String,
    },
    extend: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: [String, Object],
      default: () => ({
        open: 'fas fa-angle-down',
        close: 'fas fa-angle-down',
      }),
    },
    divider: {
      type: Boolean,
      default: false,
    },
    fill: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: Array,
      default: () => ([]),
    },
    duration: {
      type: Number,
      default: 450,
    },
    iconTransition: {
      type: Number,
      default: 1,
    },
    rotations: {
      type: Number,
      default: 0.5,
    },
    direction: {
      type: String,
      default: 'backwards',
      validator(val) {
        return ['forwards', 'backwards'].indexOf(val) !== -1;
      },
    },
    size: {
      type: Number,
      default: 1.5,
    },
  },
  data() {
    return {
      uid: `exp_${Math.random().toString(36).slice(2, 8)}`,
      isExtended: this.extend,
      isOpen: false,
      contentRef: null,
      height: 500,
    };
  },
  computed: {
    styles: {
      get() {
        return {
          '--header-height': `${this.size * 3}rem`,
          '--content-height': `${this.height}px`,
          '--transition-duration': `${this.duration}ms`,
          '--rotation-degree': `${this.getRotations()}deg`,
          '--font-size': `${this.size}rem`,
          '--layout': `'${this.layout.join(' ')}'`,
        };
      },
      set({ height }) {
        this.height = height;
      },
    },
    getPos() {
      const tl = (x) => {
        switch (this.layout.indexOf(x)) {
          case 0:
            return 'left';
          case 1:
            return 'center';
          case 2:
            return 'right';
        }
      };

      const pos = {};

      this.layout.forEach((x, i) => pos[x] = tl(x));

      return pos;
    },
  },
  mounted() {
    this.contentRef = this.$refs.content;
    if (this.extend) {
      this.extendContent();
    }
  },
  methods: {
    getRotations() {
      if (this.direction === 'forwards') {
        return (360 * this.rotations);
      }
      return (360 * this.rotations) * -1;
    },
    getIcon() {
      return this.icon instanceof Object
        ? this.isExtended
          ? this.icon.close
          : this.icon.open
        : icon;
    },
    extendContent() {
      this.styles = { height: this.contentRef.scrollHeight };
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        this.$emit('open');
      }

      setTimeout(() => {
        this.isExtended = !this.isExtended;
      }, this.duration / this.iconTransition);
    },
  },
};
</script>

<style lang="scss" scoped>
$border-color: #dee2e6;

.expansion-panel {
    &.fill {
        width: 100%;
    }

    &.divider {
        border-top: 2px solid $border-color;
    }

    &--header {
        display: flex;
        align-items: center;
        min-height: var(--header-height);
        font-size: var(--font-size);
        cursor: pointer;

        &-title {
            font-size: var(--font-size);
            padding: 1.35rem 0;
        }

        &-title.left {
            margin-right: 1rem;
        }
        &-title.center {
            margin-left: 1rem;
        }
        &-title.right {
            margin-left: 1rem;
        }

        &-title.no-filler {
            flex: 1;

            &.right,
            &.center {
                text-align: right;
            }
        }

        &-filler {
            width: 100%;
            flex: 1;
        }

        &-icon {
            .io-icon {
                display: flex;
                justify-content: center;
                align-items: center;
                width: var(--header-height);
                height: var(--header-height);
                transform: rotate(0deg);
                transition: transform var(--transition-duration) ease-in-out;
            }

            &.open .io-icon  {
                transform: rotate(var(--rotation-degree));
            }
        }
    }

    &--content {
        height: 0;
        font-size: 1.1rem;
        font-weight: 300;
        transition: height var(--transition-duration) ease-in-out;
        overflow: hidden;

        &.open{
            height: var(--content-height);
            display: block;
        }
    }
}
</style>
